import React, { createContext, useContext, useState, useCallback } from 'react';
import client from '../shopify/client';
import { CREATE_CART, ADD_CART_LINES, REMOVE_CART_LINES, UPDATE_CART_LINES } from '../shopify/queries';

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const [cart, setCart] = useState(null);
    const [cartOpen, setCartOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const normalizeCart = (rawCart) => {
        if (!rawCart) return null;
        return {
            id: rawCart.id,
            checkoutUrl: rawCart.checkoutUrl,
            lines: rawCart.lines.edges.map((e) => ({
                id: e.node.id,
                quantity: e.node.quantity,
                variantId: e.node.merchandise.id,
                variantTitle: e.node.merchandise.title,
                price: parseFloat(e.node.merchandise.price.amount),
                currency: e.node.merchandise.price.currencyCode,
                productTitle: e.node.merchandise.product.title,
                productHandle: e.node.merchandise.product.handle,
                image: e.node.merchandise.product.images.edges[0]?.node ?? null,
            })),
            total: parseFloat(rawCart.cost.totalAmount.amount),
            currency: rawCart.cost.totalAmount.currencyCode,
        };
    };

    const addToCart = useCallback(
        async (variantId, quantity = 1) => {
            setLoading(true);
            try {
                if (!cart) {
                    const { data } = await client.request(CREATE_CART, {
                        variables: { lines: [{ merchandiseId: variantId, quantity }] },
                    });
                    setCart(normalizeCart(data.cartCreate.cart));
                } else {
                    const { data } = await client.request(ADD_CART_LINES, {
                        variables: {
                            cartId: cart.id,
                            lines: [{ merchandiseId: variantId, quantity }],
                        },
                    });
                    setCart(normalizeCart(data.cartLinesAdd.cart));
                }
                setCartOpen(true);
            } finally {
                setLoading(false);
            }
        },
        [cart],
    );

    const removeFromCart = useCallback(
        async (lineId) => {
            if (!cart) return;
            setLoading(true);
            try {
                const { data } = await client.request(REMOVE_CART_LINES, {
                    variables: { cartId: cart.id, lineIds: [lineId] },
                });
                setCart(normalizeCart(data.cartLinesRemove.cart));
            } finally {
                setLoading(false);
            }
        },
        [cart],
    );

    const updateQuantity = useCallback(
        async (lineId, quantity) => {
            if (!cart) return;
            if (quantity <= 0) {
                return removeFromCart(lineId);
            }
            setLoading(true);
            try {
                const { data } = await client.request(UPDATE_CART_LINES, {
                    variables: {
                        cartId: cart.id,
                        lines: [{ id: lineId, quantity }],
                    },
                });
                setCart(normalizeCart(data.cartLinesUpdate.cart));
            } finally {
                setLoading(false);
            }
        },
        [cart, removeFromCart],
    );

    const cartCount = cart?.lines.reduce((sum, l) => sum + l.quantity, 0) ?? 0;

    return (
        <CartContext.Provider
            value={{
                cart,
                cartOpen,
                setCartOpen,
                cartCount,
                loading,
                addToCart,
                removeFromCart,
                updateQuantity,
            }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used within CartProvider');
    return ctx;
}
